import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// This component now takes the loggedInUser as a prop
const DeviceMap = ({ loggedInUser }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markers = useRef({});
    const [devices, setDevices] = useState([]);

    const geoJsonLayer = useRef(null);

    // Initialize the map once and add India GeoJSON boundary layer
    useEffect(() => {
        if (!mapInstance.current) {
            mapInstance.current = L.map(mapRef.current).setView([22.0, 79.0], 5); // Default India center
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
            }).addTo(mapInstance.current);
        }

        const loadGeoJsonLayer = async () => {
            try {
                const response = await fetch('/india-composite.geojson');
                if (!response.ok) {
                    console.error('Failed to load India GeoJSON:', response.statusText);
                    return;
                }
                const indiaGeoJson = await response.json();
                if (geoJsonLayer.current) {
                    mapInstance.current.removeLayer(geoJsonLayer.current);
                }
                geoJsonLayer.current = L.geoJSON(indiaGeoJson, {
                    // Boundary is now purely visual — prevents click/hover events
                    // from bubbling up and interfering with device markers
                    interactive: false,
                    style: {
                        color: '#1f78b4',
                        weight: 2,
                        opacity: 0.8,
                        fillColor: '#1f78b4',
                        fillOpacity: 0.1,
                    },
                }).addTo(mapInstance.current);

                const bounds = geoJsonLayer.current.getBounds();
                if (bounds.isValid()) {
                    mapInstance.current.fitBounds(bounds, { padding: [20, 20] });
                }
            } catch (err) {
                console.error('Error loading India GeoJSON:', err);
            }
        };

        loadGeoJsonLayer();
    }, []);

    // Fetch and update device data periodically
    useEffect(() => {
        if (!loggedInUser) return; // Don't fetch if no user is logged in

        const fetchDeviceData = async () => {
            try {
                const response = await fetch('http://52.66.78.81:4000/api/devices', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user: loggedInUser })
                });
                const data = await response.json();
                if (response.ok) {
                    setDevices(data);
                } else {
                    console.error('Failed to fetch devices:', data.message);
                }
            } catch (err) {
                console.error('Error fetching device status:', err);
            }
        };

        fetchDeviceData();
        const interval = setInterval(fetchDeviceData, 5000);

        return () => clearInterval(interval);
    }, [loggedInUser]); // Re-run this effect if the loggedInUser changes

    // Update markers on the map when device data changes
    useEffect(() => {
        if (!mapInstance.current || devices.length === 0) return;

        devices.forEach((device) => {
            const { device_id, lat, lon, status, owner, display_name } = device;
            const isOnline = status === 'online';
            const markerColor = isOnline ? '#22c55e' : '#f97316'; // green / orange
            const deviceLabel = display_name || device_id;

            const tooltipContent = `
                <div style="font-size: 13px; line-height: 1.4;">
                    <strong>${deviceLabel}</strong><br/>
                    Status: <span style="color: ${markerColor}; font-weight: 600;">
                        ${isOnline ? 'Online' : 'Offline'}
                    </span>
                    ${owner ? `<br/>Owner: ${owner}` : ''}
                </div>
            `;

            if (!markers.current[device_id]) {
                const marker = L.circleMarker([lat, lon], {
                    color: '#ffffff',        // white halo border
                    weight: 2,
                    radius: 9,
                    fillColor: markerColor,
                    fillOpacity: 0.9,
                    bubblingMouseEvents: false, // prevents events from reaching layers underneath
                }).addTo(mapInstance.current);

                // Hover tooltip
                marker.bindTooltip(tooltipContent, {
                    direction: 'top',
                    offset: [0, -10],
                    opacity: 1,
                    sticky: true,
                    className: 'device-tooltip',
                });

                // Visual feedback on hover
                marker.on('mouseover', function (e) {
                    L.DomEvent.stopPropagation(e);
                    this.setStyle({ radius: 12, weight: 3 });
                });
                marker.on('mouseout', function () {
                    this.setStyle({ radius: 9, weight: 2 });
                });
                marker.on('click', function (e) {
                    L.DomEvent.stopPropagation(e);
                });

                markers.current[device_id] = marker;
            } else {
                const marker = markers.current[device_id];
                marker.setStyle({ fillColor: markerColor });
                marker.setTooltipContent(tooltipContent);
            }
        });
    }, [devices]);

    return (
        <div style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
            <h2 style={{ textAlign: 'center', color: '#2d3748' }}>Device Map</h2>
            <div ref={mapRef} style={{ width: '100%', height: '400px', borderRadius: '8px' }} />
        </div>
    );
};

export default DeviceMap;